import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter } from '@angular/core';
import pickBy from 'lodash-es/pickBy';
import { Observable } from 'rxjs';

import { Room, GuestRoom } from '@shared/base-search-bar/entities/room.entities';
import { TravelerTypes } from '@pax/entities/traveler-info';
import { TagMemberType } from '@design-system/components/molecules/tag-member/entities/tag-member.types';
import {
  PriceDetailCardDiscount, PriceDetailCardPrice, PriceDetailCardSubtitle
} from '@design-system/components/organisms/price-detail-card/entities/price-detail-card.types';
import {
  getPaymentDiscountAmount, getPaymentPercentageCalculated, getStrikethroughDiscount
} from '@shared/payment-detail/mappers/discount-calculated.mapper';
import { PriceDetailService } from '@shared/payment-detail/services/payment-detail.service';
import { Price } from '@app/entities/price';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';

@Component({
  selector: 'app-hotel-payment-detail',
  templateUrl: './hotel-payment-detail.component.html',
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class HotelPaymentDetailComponent {

  @Input() guestsPerRoom: Room[];
  @Input() amount: Price;
  @Input() text: string;
  @Input() conversionRate: number;

  @Input() pricePerNights: number;
  @Input() nights: number;
  @Input() hours: number;
  @Input() isModal = false;
  @Input() isMobile = false;
  @Input() showSwipeUp = false;
  @Input() productReference: number;
  @Input() isByhours: boolean;
  @Input() capacity: number;

  @Output() toggleShowSwipeUp: EventEmitter<Event> = new EventEmitter<Event>();

  public readonly tagMemberType = TagMemberType;

  constructor(
    private readonly service: PriceDetailService,
  ) { }

  get discount$(): Observable<PriceDetailCardDiscount> {
    return this.service.getCardDiscount$(
      SearchBy.HOTELS, this.discountAmount,
      this.discountPercentageValueProp, this.amount?.CurrencyCode
    );
  }

  get rows(): PriceDetailCardPrice[][] {
    const sections = [
      [
        this.service.getCardPricePerNightOrHoursItem(
          this.isByhours, this.isByhours ? this.pricePerHours : this.pricePerNights, this.amount?.CurrencyCode
        )
      ],
      [
        this.service.getCardAmountPerNightOrHoursItem(
          this.isByhours, this.nights, this.totalGuests, this.hours, this.capacity,
          this.amount?.Amount, this.amount?.CurrencyCode
        ),
        ...this.service.getCardExtendedPaymentDetailSection(
          this.amount?.Taxes, this.amount?.Fees, this.amount?.Surcharges,
          this.amount?.CommissionAmount, this.amount?.CurrencyCode
        )
      ]
    ];
    return sections;
  }

  get subtitles(): PriceDetailCardSubtitle[] {
    return this.service.getCardSubtitlesByGuests(this.mapGuestsPerRoom, this.isByhours, this.capacity);
  }

  get mapGuestsPerRoom(): GuestRoom[] {
    return this.guestsPerRoom ? this.guestsPerRoom.map(room => {
      const adults = room[TravelerTypes.ADULTS];
      const children = room[TravelerTypes.CHILDREN];
      const ages = room.childrenAges;
      const newRoom = { [TravelerTypes.ADULTS]: adults, [TravelerTypes.CHILDREN]: children, childrenAges: ages };
      return pickBy(newRoom, this.isValidGuest);
    }) : [];
  }

  get totalGuests(): number {
    const { totalAdults, totalChildren } = this.guestsPerRoom.reduce(
      (accumulator, { ADULTS, CHILDREN }) => ({
        totalAdults: accumulator.totalAdults + ADULTS,
        totalChildren: accumulator.totalChildren + CHILDREN,
      }),
      { totalAdults: 0, totalChildren: 0 }
    );
    return this.isByhours ? this.capacity : totalAdults + totalChildren;
  }

  get pricePerHours() {
    return this.amount?.Total / this.hours;
  }

  get multipleGuestByhours(): boolean {
    return this.capacity > 1;
  }

  get hasTaxes(): boolean {
    return this.amount.Taxes > 0;
  }

  get discountAmount(): number {
    return getPaymentDiscountAmount(
      this.amount?.Strikethrough, this.amount?.Total, this.amount?.DiscountAmount, this.strikethroughDiscount
    );
  }

  get discountPercentageValueProp(): number {
    return getPaymentPercentageCalculated(
      true, this.amount?.Strikethrough, this.amount?.Total, this.amount?.DiscountAmount,
      this.strikethroughDiscount, this.amount?.Amount, this.amount?.Taxes,
    );
  }

  private get strikethroughDiscount(): number {
    return getStrikethroughDiscount(this.amount?.TotalDiscountInStrikethrough, this.amount?.TotalDiscount);
  }

  private isValidGuest(guest: number): boolean {
    return guest > 0;
  }

}
